import React, { useState, useEffect } from "react";
import {
  Badge,
  Tooltip,
  Button,
  DatePicker,
  Space,
  Dropdown,
  Menu,
  Divider,
} from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  CalendarOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { getNoticeDetails } from "../api/apiService";
import { DASHBOARD } from "../api/endpointMap";

const Calender = ({ portal }) => {
  const [dueNotices, setDueNotices] = useState({});
  const [currentDate, setCurrentDate] = useState(moment());
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const currentMonth = currentDate.format("MMMM YYYY");
  const startOfMonth = moment(currentDate).startOf("month");
  const endOfMonth = moment(currentDate).endOf("month");
  const today = moment().format("YYYY-MM-DD");

  const handlePrevMonth = () => {
    const newDate = moment(currentDate).subtract(1, "months");
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = moment(currentDate).add(1, "months");
    setCurrentDate(newDate);
  };

  const handleDateChange = (date) => {
    if (date) {
      setCurrentDate(date);
    }
  };

  const jumpToToday = () => {
    setCurrentDate(moment());
  };

  const getCalendarData = async () => {
    try {
      setLoading(true);

      // IT uses legacy Frappe envelope `{message: {message: [...]}}` with
      // month/year + portal params. GST/TDS use new envelope `{data: [...]}`
      // with month_start_date/month_end_date params. Both endpoints already
      // exist in BE; we just route to the right one based on portal prop.
      if (portal === "Income Tax") {
        const year = currentDate.year();
        const month = currentDate.month() + 1;
        const data = await getNoticeDetails(
          `${DASHBOARD.CALENDAR_DUES}?month=${month}&year=${year}&portal=${portal}`
        );
        setCalendarData(data?.message?.message || []);
      } else {
        const startDate = moment(currentDate)
          .startOf("month")
          .format("YYYY-MM-DD");
        const endDate = moment(currentDate).endOf("month").format("YYYY-MM-DD");
        const url =
          portal === "GST"
            ? DASHBOARD.CALENDAR_DUES_GST
            : DASHBOARD.CALENDAR_DUES_TDS;
        const data = await getNoticeDetails(
          `${url}?month_start_date=${startDate}&month_end_date=${endDate}`
        );
        setCalendarData(data?.data || []);
      }
    } catch (err) {
      console.error("Error fetching calendar data:", err);
      setCalendarData([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  useEffect(() => {
    setCalendarData([]);
    setDueNotices([]);
    getCalendarData();
  }, [currentDate]);

  useEffect(() => {
    const newDueNotices = {};

    if (calendarData.length) {
      calendarData.forEach((item) => {
        const date = moment(item.due_date).date();
        newDueNotices[date] = item.count;
      });
      setDueNotices(newDueNotices);
    } else {
      setDueNotices([]);
    }
  }, [calendarData]);

  const handleDateClick = (date) => {
    const formattedDate = moment(currentDate).date(date).format("YYYY-MM-DD");
    navigate(`notices?type=total_notices&due_date=${formattedDate}`);
  };

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const daysInMonth = Array.from(
    { length: endOfMonth.date() },
    (_, i) => i + 1
  );

  const padding = startOfMonth.day();

  // Generate quick navigation items for years
  const currentYear = moment().year();

  const yearItems = Array.from({ length: 18 }, (_, i) => {
    const year = currentYear - 14 + i;
    return {
      key: `year-${year}`,
      label: year.toString(),
      onClick: () => {
        const newDate = moment(currentDate).year(year);
        setCurrentDate(newDate);
      },
    };
  });

  // Generate quick navigation items for months
  const monthItems = Array.from({ length: 12 }, (_, i) => {
    return {
      key: `month-${i}`,
      label: moment().month(i).format("MMMM"),
      onClick: () => {
        const newDate = moment(currentDate).month(i);
        setCurrentDate(newDate);
      },
    };
  });

  return (
    <div className="bg-white shadow-md rounded-lg p-4">
      <div className="flex flex-col mb-4">
        {/* Month navigation */}
        <div className="flex justify-center items-center">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={handlePrevMonth}
            className="text-blue-900 hover:text-blue-700 exempt-from-readonly "
          />
          <h2 className="text-lg font-semibold text-blue-900 mx-2">
            {currentMonth} [Due Notices]
          </h2>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={handleNextMonth}
            className="text-blue-900 hover:text-blue-700 exempt-from-readonly"
          />
        </div>

        <Divider style={{ margin: "5px" }} />
        {/* Advanced navigation bar */}
        <div className="flex justify-between items-center mb-2">
          <Space>
            <Dropdown
              menu={{
                items: yearItems,
                className: "max-h-64 overflow-y-auto",
              }}
              placement="bottomLeft"
              className="exempt-from-readonly"
            >
              <Button className="text-blue-900">
                {currentDate.format("YYYY")} <DownOutlined />
              </Button>
            </Dropdown>

            <Dropdown
              menu={{
                items: monthItems,
              }}
              placement="bottomLeft"
              className="exempt-from-readonly"
            >
              <Button className="text-blue-900">
                {currentDate.format("MMMM")} <DownOutlined />
              </Button>
            </Dropdown>
          </Space>

          <Space>
            <Button
              type="primary"
              onClick={jumpToToday}
              className="bg-blue-900 hover:bg-blue-700 exempt-from-readonly"
            >
              Current Month
            </Button>
          </Space>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 text-center text-gray-600 font-bold">
        {weekdays.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingOutlined spin style={{ fontSize: "35px" }} />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2 mt-2 text-center">
          {Array.from({ length: padding }).map((_, index) => (
            <div key={`empty-${index}`} className="p-1"></div>
          ))}

          {daysInMonth.map((date) => {
            const fullDate = moment(currentDate)
              .date(date)
              .format("YYYY-MM-DD");
            const isToday = fullDate === today;

            return (
              <Tooltip
                key={date}
                title={`${currentDate.format(
                  "MMMM"
                )} ${date}, ${currentDate.year()}: ${
                  dueNotices[date] || 0
                } due notice${dueNotices[date] !== 1 ? "s" : ""}`}
              >
                <div
                  className={`p-1 rounded-md hover:bg-blue-100 cursor-pointer flex flex-col items-center`}
                  onClick={() => handleDateClick(date)}
                >
                  <Badge count={dueNotices[date] || 0}>
                    <div
                      style={{ borderWidth: "2px" }}
                      className={`border w-8 h-8 flex items-center justify-center rounded-full border-solid ${
                        isToday
                          ? "bg-blue-900 font-bold text-white border-blue-900"
                          : "font-medium text-gray-500 border-blue-900"
                      }`}
                    >
                      {date}
                    </div>
                  </Badge>
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Calender;
